import { Button, Card, CardContent, Spinner } from '@1trading/react';
import { ArrowLeft, Ban, CircleAlert, Play, RotateCcw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { confirmAction } from '@/components/dialogs/confirm-action';
import { EmptyState } from '@/components/feedback/empty-state';
import { QueryErrorBanner } from '@/components/feedback/query-error';
import { LabeledValue } from '@/components/ui/labeled-value';
import { LoadingPanel } from '@/components/ui/loading-panel';
import { StatsCard } from '@/components/ui/stats-card';
import { parseApproveError } from '@/features/fee-runs/approve-error';
import { FeeRunGuards } from '@/features/fee-runs/fee-run-guards';
import { feeRunActions, formatChargeDate, formatEurString, isActiveFeeRunStatus } from '@/features/fee-runs/fee-run-helpers';
import { FeeRunProgress } from '@/features/fee-runs/fee-run-progress';
import { FeeRunReports } from '@/features/fee-runs/fee-run-reports';
import { FeeRunStatusChip } from '@/features/fee-runs/fee-run-status-chip';
import { FeeRunUsersTable } from '@/features/fee-runs/fee-run-users-table';
import { FEE_RUN_DETAIL_ROUTE } from '@/features/fee-runs/fee-runs-panel';
import { useApproveFeeRun, useCancelFeeRun, useFeeRun, useRetryFeeRun } from '@/hooks/queries/fees/use-fee-runs';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { getErrorMessage, isForbiddenError, isNotFoundError } from '@/lib/api/error';
import type { FeeRun } from '@/services/api/fee-runs';
import { formatDate } from '@/utils/format';
import { formatSnakeCase } from '@/utils/string';

// The API demands a fuller justification than the shared dialog's default minimum: it is the audit record for a money movement
const APPROVAL_JUSTIFICATION_MIN = 35;

const ExclusionsSummary = ({ run }: { run: FeeRun }) => {
  const entries = Object.entries(run.exclusionCounts ?? {}).sort((a, b) => b[1] - a[1]);
  if (!entries.length) return null;
  return (
    <Card>
      <CardContent className="flex flex-col gap-2 pt-6">
        <span className="text-sm font-medium">Not charged</span>
        <ul className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-muted-foreground">
          {entries.map(([reason, count]) => (
            <li className="tabular-nums" key={reason}>
              <span className="font-medium text-foreground">{count.toLocaleString('en-GB')}</span> {formatSnakeCase(reason).toLowerCase()}
            </li>
          ))}
        </ul>
        <p className="text-xs text-muted-foreground">Every exclusion is listed with its reason in the Exclusions report.</p>
      </CardContent>
    </Card>
  );
};
ExclusionsSummary.displayName = 'ExclusionsSummary';

/** One fee run end to end: review the calculation, approve it, watch it collect, download the reports. */
export const FeeRunDetail = ({ runId }: { runId: string }) => {
  const runQuery = useFeeRun(runId);
  const approveMutation = useApproveFeeRun(runId);
  const cancelMutation = useCancelFeeRun(runId);
  const retryMutation = useRetryFeeRun(runId);
  const run = runQuery.data?.run;
  const capabilities = runQuery.data?.capabilities;

  useDocumentTitle(run ? `Fee run · ${formatChargeDate(run)}` : 'Fee run');

  const isNotFound = runQuery.isError && isNotFoundError(runQuery.error);
  const isForbidden = runQuery.isError && isForbiddenError(runQuery.error);

  const handleApprove = async () => {
    if (!run?.totals) return;
    let startedWithGaps: string | null = null;
    const justification = await confirmAction({
      title: 'Collect these fees?',
      description: `${run.totals.users.toLocaleString('en-GB')} account holders will be debited a total of ${formatEurString(run.totals.finalFeeEur)} on ${formatChargeDate(run)}. This cannot be undone. Write at least ${APPROVAL_JUSTIFICATION_MIN} characters explaining what you reviewed.`,
      actionLabel: 'Collect fees',
      destructive: true,
      onConfirm: async (text) => {
        if (text.trim().length < APPROVAL_JUSTIFICATION_MIN) throw new Error(`The justification must be at least ${APPROVAL_JUSTIFICATION_MIN} characters.`);
        try {
          await approveMutation.mutateAsync(text.trim());
        } catch (error) {
          const parsed = await parseApproveError(error);
          // The run is collecting; the gap is a Retry away, so the dialog closes and the operator is told rather than blocked
          if (parsed.kind === 'enqueue-incomplete') {
            startedWithGaps = parsed.message;
            return;
          }
          throw new Error(parsed.message);
        }
      },
    });
    if (justification === null) return;
    if (startedWithGaps) toast.warning(startedWithGaps);
    else toast.success('Collection started');
  };

  const handleCancel = async () => {
    const confirmed = await confirmAction({
      title: 'Discard this run?',
      description: 'The calculation and its reports stay available, but the run can no longer be approved.',
      actionLabel: 'Discard run',
      destructive: true,
      requireJustification: false,
      onConfirm: () => cancelMutation.mutateAsync(),
    });
    if (confirmed !== null) toast.success('Run discarded');
  };

  const handleRetry = async () => {
    const confirmed = await confirmAction({
      title: 'Retry unfinished account holders?',
      description: 'Every account holder who is not done is queued again. Nothing already charged is charged twice — each resumes from its own record.',
      actionLabel: 'Retry',
      requireJustification: false,
      onConfirm: async () => {
        try {
          await retryMutation.mutateAsync();
        } catch (error) {
          throw new Error(isForbiddenError(error) ? 'You are not authorised to retry fee runs.' : getErrorMessage(error));
        }
      },
    });
    if (confirmed !== null) toast.success('Retry queued');
  };

  if (runQuery.isPending) return <LoadingPanel className="py-24" />;

  if (isNotFound || isForbidden) {
    return (
      <EmptyState
        action={
          <Button asChild type="button" variant="outline">
            <Link to={FEE_RUN_DETAIL_ROUTE}>Back to fee collection</Link>
          </Button>
        }
        icon={CircleAlert}
        title={isForbidden ? 'Fee runs are limited to the fee operators group.' : 'Fee run not found.'}
      />
    );
  }

  if (runQuery.isError || !run) {
    return <QueryErrorBanner error={runQuery.error} onRetry={() => runQuery.refetch()} title="Failed to fetch the fee run" />;
  }

  const actions = feeRunActions(run.status);
  const canApprove = capabilities?.canApprove ?? false;
  const isActive = isActiveFeeRunStatus(run.status);
  const hasCollection = run.status === 'COLLECTING' || run.status === 'FINALISING' || run.status === 'COMPLETED' || run.status === 'PARTIAL';

  return (
    <div className="flex flex-col gap-4">
      <Link className="inline-flex w-fit items-center gap-1 text-sm text-muted-foreground hover:text-foreground" to={FEE_RUN_DETAIL_ROUTE}>
        <ArrowLeft className="size-4" />
        Back to fee collection
      </Link>

      <header className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-3xl font-semibold">{formatChargeDate(run)}</h1>
            <FeeRunStatusChip status={run.status} />
            {isActive ? <Spinner className="size-4" /> : null}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {actions.canApprove ? (
              <Button className="gap-2" disabled={!canApprove || run.approvable === false || approveMutation.isPending} onClick={() => void handleApprove()} type="button">
                <Play className="size-4" />
                Collect fees
              </Button>
            ) : null}
            {actions.canRetry && !isActive ? (
              <Button className="gap-2" disabled={!canApprove || retryMutation.isPending} onClick={() => void handleRetry()} type="button" variant="outline">
                <RotateCcw className="size-4" />
                Retry
              </Button>
            ) : null}
            {actions.canCancel ? (
              <Button className="gap-2" disabled={!canApprove || cancelMutation.isPending} onClick={() => void handleCancel()} type="button" variant="outline">
                <Ban className="size-4" />
                Discard
              </Button>
            ) : null}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
          <LabeledValue label="Fees">{run.params.feeScope === 'CUSTODY' ? 'Custody (OTE)' : 'Platform (OTM)'}</LabeledValue>
          <LabeledValue label="Requested">
            {run.requestedBy} · {formatDate(run.requestedAt)}
          </LabeledValue>
          {run.calculatedAt ? <LabeledValue label="Calculated">{formatDate(run.calculatedAt)}</LabeledValue> : null}
          {run.approvedBy ? (
            <LabeledValue label="Approved">
              {run.approvedBy} · {formatDate(run.approvedAt)}
            </LabeledValue>
          ) : null}
          {run.completedAt ? <LabeledValue label="Completed">{formatDate(run.completedAt)}</LabeledValue> : null}
          <LabeledValue label="Run id" mono>
            {run.runId}
          </LabeledValue>
        </div>
        {actions.canApprove && !canApprove ? <p className="text-xs text-muted-foreground">Approving a run is limited to the fee approvers group.</p> : null}
        {run.approvalJustification ? <p className="text-sm text-muted-foreground">“{run.approvalJustification}”</p> : null}
        {run.error ? <p className="text-sm text-red-500">{run.error}</p> : null}
      </header>

      {run.status === 'CALCULATING' ? (
        <Card>
          <CardContent className="flex items-center gap-3 pt-6 text-sm text-muted-foreground">
            <Spinner className="size-4" />
            Reading balances, activity and terms across the platform. This takes a few minutes; the page updates on its own.
          </CardContent>
        </Card>
      ) : null}

      {run.totals ? (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatsCard label="Account holders" value={run.totals.users.toLocaleString('en-GB')} />
          <StatsCard label="Charges" value={run.totals.charges.toLocaleString('en-GB')} />
          <StatsCard label="Fees to collect" subtitle={`from ${formatEurString(run.totals.totalEurBalance)} in scope`} value={formatEurString(run.totals.finalFeeEur)} />
          <StatsCard
            label="Exchange / instant trade"
            subtitle={`${run.totals.bySource.fast.charges.toLocaleString('en-GB')} exchange charges · ${run.totals.bySource['instant-trade'].charges.toLocaleString('en-GB')} instant trade`}
            value={`${formatEurString(run.totals.bySource.fast.feeEur)} / ${formatEurString(run.totals.bySource['instant-trade'].feeEur)}`}
          />
        </div>
      ) : null}

      {hasCollection ? <FeeRunProgress run={run} /> : null}

      {run.collected ? (
        <Card>
          <CardContent className="flex flex-wrap items-center gap-x-6 gap-y-2 pt-6 text-sm">
            <span>
              Collected <span className="font-semibold">{formatEurString(run.collected.feeEur)}</span> from {run.collected.users.toLocaleString('en-GB')} account holders
            </span>
            {run.collected.partialUsers > 0 ? <span className="text-amber-500">{run.collected.partialUsers} did not fully settle — see the Collected charges report</span> : null}
            {run.counters.chargesUnknown > 0 ? <span className="text-amber-500">{run.counters.chargesUnknown} unconfirmed — Retry to reconcile</span> : null}
          </CardContent>
        </Card>
      ) : null}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {run.guards?.length ? <FeeRunGuards approvable={run.approvable} guards={run.guards} /> : null}
        <FeeRunReports run={run} />
      </div>

      <ExclusionsSummary run={run} />

      {run.totals ? <FeeRunUsersTable isRunActive={isActive} runId={runId} /> : null}
    </div>
  );
};
FeeRunDetail.displayName = 'FeeRunDetail';
